import React, { Fragment, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  useCreateMarktCustomerMutation,
  useCreateMarktStockMovementMutation,
  useOpenCashRegisterMutation,
} from '../../../redux/api/marktApi';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setCashRegisterSession } from '../../../redux/authSlice';
import { useApiErrorToast } from '../../../hooks/useApiError';

const CreateCustomerModal = ({ initialValues, onCreated }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedsession] = useState(null);

  const [doCreateCustomer, { isLoading }] = useCreateMarktCustomerMutation();
  const apiErrorToast = useApiErrorToast();
  const [fieldErrors, setFieldErrors] = useState({});

  const dispatch = useDispatch();

  const defaultFormValues = {
    name: '',
    contact: '',
    contact_email: '',
    address: '',
    zip: '',
    city: '',
    country: 'Deutschland',
  };

  const [formValues, setFormValues] = useState(defaultFormValues);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    // Only name and email are mandatory now.
    if (!formValues.name || !formValues.contact_email) {
      toast.error('Bitte Name und E-Mail angeben!');
      return;
    }
    doCreateCustomer(formValues)
      .unwrap()
      .then((created) => {
        toast.success('Kunde angelegt!');
        setShowModal(false);
        onCreated?.(created);
      })
      .catch((err) => {
        const e = err?.data || err;
        if (e?.code === 'validation_error' && e.details) {
          setFieldErrors(e.details);
        }
        apiErrorToast(err, 'Kunde konnte nicht angelegt werden');
      });
  };

  const handleOpen = () => {
    // Prefill from a reservation (name/email) when provided, falling back to defaults.
    setFormValues({ ...defaultFormValues, ...(initialValues || {}) });
    setShowModal(true);
  };

  return (
    <Fragment>
      <button className='btn btn-outline-primary' onClick={handleOpen}>
        Kunden anlegen
      </button>
      <Modal show={showModal} backdrop={'static'}>
        <Modal.Header>
          <Modal.Title>Getränkemarkt Kunden anlegen</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {Object.keys(fieldErrors).length > 0 && (
              <div className='alert alert-danger'>
                <ul className='mb-0 ps-3'>
                  {Object.entries(fieldErrors).map(([field, msgs]) => (
                    <li key={field}>
                      <strong>{field === '_' || field === 'non_field_errors' ? '' : `${field}: `}</strong>
                      {Array.isArray(msgs) ? msgs.join(' ') : String(msgs)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className='mb-3'>
              <label htmlFor='name' className='form-label'>
                Firma/Verein/Person <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                id='name'
                name='name'
                value={formValues.name}
                onChange={(e) =>
                  setFormValues({ ...formValues, name: e.target.value })
                }
                placeholder='Kulturverein e.V.'
                required
              />
            </div>

            <div className='mb-3'>
              <label htmlFor='contact' className='form-label'>
                Kontaktperson (Name) <span className='text-muted'>(optional)</span>
              </label>
              <input
                type='text'
                className='form-control'
                id='contact'
                name='contact'
                value={formValues.contact}
                onChange={(e) =>
                  setFormValues({ ...formValues, contact: e.target.value })
                }
                placeholder='Anna Musterfrau'
              />
            </div>

            <div className='mb-3'>
              <label htmlFor='contact_email' className='form-label'>
                E-Mail <span className='text-danger'>*</span>
              </label>
              <input
                type='email'
                className='form-control'
                id='contact_email'
                name='contact_email'
                value={formValues.contact_email}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    contact_email: e.target.value,
                  })
                }
                placeholder='anna.musterfrau@example.com'
                required
              />
            </div>

            <div className='mb-3'>
              <label htmlFor='address' className='form-label'>
                Straße und Hausnummer{' '}
                <span className='text-muted'>(optional)</span>
              </label>
              <input
                type='text'
                className='form-control'
                id='address'
                name='address'
                value={formValues.address}
                onChange={(e) =>
                  setFormValues({ ...formValues, address: e.target.value })
                }
                placeholder='Musterstraße 123'
              />
            </div>

            <div className='row g-3'>
              <div className='col-md-4'>
                <label htmlFor='zip' className='form-label'>
                  PLZ <span className='text-muted'>(optional)</span>
                </label>
                <input
                  type='text'
                  className='form-control'
                  id='zip'
                  name='zip'
                  value={formValues.zip}
                  onChange={(e) =>
                    setFormValues({ ...formValues, zip: e.target.value })
                  }
                  placeholder='12345'
                />
              </div>
              <div className='col-md-8'>
                <label htmlFor='city' className='form-label'>
                  Ort <span className='text-muted'>(optional)</span>
                </label>
                <input
                  type='text'
                  className='form-control'
                  id='city'
                  name='city'
                  value={formValues.city}
                  onChange={(e) =>
                    setFormValues({ ...formValues, city: e.target.value })
                  }
                  placeholder='Musterstadt'
                />
              </div>
            </div>

            <div className='mb-3 mt-3'>
              {' '}
              {/* mt-3 for spacing from above row */}
              <label htmlFor='country' className='form-label'>
                Land <span className='text-muted'>(optional)</span>
              </label>
              <input
                type='text'
                className='form-control'
                id='country'
                name='country'
                value={formValues.country}
                onChange={(e) =>
                  setFormValues({ ...formValues, country: e.target.value })
                }
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='secondary'
              onClick={() => setShowModal(false)}
              disabled={isLoading}
            >
              abbrechen
            </Button>
            <Button variant='success' type='submit'>
              Kunden speichern
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </Fragment>
  );
};

export default CreateCustomerModal;
